import { Divider, IconButton, Typography } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import type { CreativeCredit } from 'sr-types/lib/shortlist/v1/graphql';
import { shortlistLineitemToInput } from '../../../../shortlists/shortlist';
import { useAddShortlistLineItem } from '../../../../shortlists/shortlistApi';
import LeadAIProcessing from '../../../../supply/opportunity/leads/LeadAIProcessing';
import type { SecuredActionType } from '../../../components/ActionType';
import SidePanel from '../../../components/layout/SidePanel';
import { useSaveCreativeCredits } from '../../../components/person/person';
import I18n, { useI18n } from '../../../i18n/I18n';
import { Icons } from '../../../icons/Icons';
import { H, V } from '../../../Layout';
import { useStyling } from '../../../Theme';
import { failedToastMessage, successToastMessage } from '../../../utils/commonUtils';
import { saveCreditsToCandidateProfile } from '../../data/profileUpdate';
import { useExtractCreativeCreditsApi } from '../../data/useExtractCreativeCreditsApi';
import { coerceKeyCreditsList } from '../../data/utils';
import type {
    KeyCreditsAIPanelProps,
    KeyCreditsLineItemRow,
    ProfileUpdateMode,
    UploadedCreditSourceFile
} from '../../types';
import { getKeyCreditsPersonId, isProfileEligibleForCreditUpdate, mergeKeyCredits } from '../utils';
import KeyCreditsReviewStep from './internal/KeyCreditsReviewStep';
import KeyCreditsSourceFooter from './internal/KeyCreditsSourceFooter';
import KeyCreditsSourceStep from './internal/KeyCreditsSourceStep';
import ProfileUpdateModeSelect from './internal/ProfileUpdateModeSelect';

type PanelStep = 'source' | 'processing' | 'review';

export default function KeyCreditsAIPanel({
    lineItem,
    shortlistId,
    expandView = false,
    setExpandView,
    activeOrganizationAccount,
    onClose,
    onSaveSuccess,
    onMergeCredits,
    variant = 'sidePanel'
}: KeyCreditsAIPanelProps) {
    const { isMobile } = useStyling();
    const configureSubtitle = useI18n('shortlist.key.credits.ai.configure');
    const rankedSubtitle = useI18n('shortlist.key.credits.ai.ranked');
    const isInline = variant === 'inline';

    const [step, setStep] = useState<PanelStep>('source');
    const [sourceText, setSourceText] = useState('');
    const [uploadedFile, setUploadedFile] = useState<UploadedCreditSourceFile | null>(null);
    const [extractedCredits, setExtractedCredits] = useState<CreativeCredit[]>([]);
    const [profileUpdateMode, setProfileUpdateMode] = useState<ProfileUpdateMode>('all');
    const [isSaving, setIsSaving] = useState(false);

    const { extractCreativeCredits } = useExtractCreativeCreditsApi(activeOrganizationAccount);
    const { addShortlistLineItem } = useAddShortlistLineItem(activeOrganizationAccount);
    const { saveCreativeCredits } = useSaveCreativeCredits();

    const isProfileEligible = isProfileEligibleForCreditUpdate(lineItem);
    const personId = getKeyCreditsPersonId(lineItem.itemReference);

    const candidateName = lineItem.name ?? '';
    const lineItemId = lineItem.identity?.id ?? '';

    const panelTitle = useMemo(
        () => (
            <V gap={0.25} sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={600} noWrap>
                    {candidateName}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                    {step === 'source' ? configureSubtitle : rankedSubtitle}
                </Typography>
            </V>
        ),
        [candidateName, configureSubtitle, rankedSubtitle, step]
    );

    const hasSource = Boolean(sourceText.trim()) || Boolean(uploadedFile?.storageFileId);

    const headerActions = useMemo((): SecuredActionType[] => {
        const actions: SecuredActionType[] = [];

        if (!isMobile && !isInline && setExpandView) {
            actions.push({
                icon: !expandView ? Icons.ExpandIcon : Icons.ShrinkIcon,
                label: <I18n token={!expandView ? 'dialog.expand' : 'dialog.shrink'} />,
                onClick: () => setExpandView((prev) => !prev)
            });
        }

        if (!isInline) {
            actions.push({
                icon: Icons.Close,
                label: <I18n token="dialog.close" />,
                onClick: onClose
            });
        }

        return actions;
    }, [expandView, isInline, isMobile, onClose, setExpandView]);

    const handleExtract = useCallback(async () => {
        if (!hasSource) {
            failedToastMessage('shortlist.key.credits.ai.no.source');
            return;
        }

        setStep('processing');
        try {
            const credits = await extractCreativeCredits({
                text: sourceText.trim() || undefined,
                storageFileId: uploadedFile?.storageFileId || undefined
            });
            if (!credits.length) {
                failedToastMessage('shortlist.key.credits.ai.extract.empty');
                setStep('source');
                return;
            }
            setExtractedCredits(credits);
            setStep('review');
        } catch {
            failedToastMessage('shortlist.key.credits.ai.extract.error');
            setStep('source');
        }
    }, [extractCreativeCredits, hasSource, sourceText, uploadedFile?.storageFileId]);

    const updateCandidateProfile = useCallback(
        async (selectedCredits: CreativeCredit[]) => {
            const creditsForProfile = profileUpdateMode === 'all' ? extractedCredits : selectedCredits;

            try {
                const profileUpdated = await saveCreditsToCandidateProfile({
                    personId,
                    isProfileEligible,
                    profileUpdateMode,
                    creditsToMerge: creditsForProfile,
                    saveCreativeCredits
                });
                if (profileUpdated) {
                    successToastMessage('shortlist.key.credits.profile.update.success');
                }
            } catch {
                failedToastMessage('shortlist.key.credits.ai.profile.update.error');
            }
        },
        [extractedCredits, isProfileEligible, personId, profileUpdateMode, saveCreativeCredits]
    );

    const handleSave = useCallback(
        async (_selectedIds: string[], selectedCredits: CreativeCredit[]) => {
            const existing = coerceKeyCreditsList(lineItem.keyCredits);
            const mergedKeyCredits = mergeKeyCredits(existing, selectedCredits);

            setIsSaving(true);
            try {
                if (onMergeCredits) {
                    onMergeCredits(mergedKeyCredits);
                    await updateCandidateProfile(selectedCredits);
                    successToastMessage('shortlist.key.credits.ai.add.success');
                    onClose();
                    return;
                }

                if (!lineItemId || !shortlistId) return;

                const updatedLineItem: KeyCreditsLineItemRow = {
                    ...lineItem,
                    keyCredits: mergedKeyCredits as CreativeCredit[]
                };

                await addShortlistLineItem({
                    shortlistId,
                    itemInput: shortlistLineitemToInput(updatedLineItem),
                    onSuccess: () => {
                        onSaveSuccess?.(lineItemId, mergedKeyCredits);
                        successToastMessage('shortlist.key.credits.ai.add.success');
                    }
                });
                await updateCandidateProfile(selectedCredits);
                onClose();
            } catch {
                failedToastMessage('shortlist.line.items.save.error');
            } finally {
                setIsSaving(false);
            }
        },
        [
            addShortlistLineItem,
            lineItem,
            lineItemId,
            onClose,
            onMergeCredits,
            onSaveSuccess,
            shortlistId,
            updateCandidateProfile
        ]
    );

    const profileUpdateFooterAccessory = useMemo(
        () =>
            isProfileEligible ? (
                <ProfileUpdateModeSelect value={profileUpdateMode} onChange={setProfileUpdateMode} />
            ) : undefined,
        [isProfileEligible, profileUpdateMode]
    );

    const handleBackFromReview = useCallback(() => {
        setStep('source');
    }, []);

    const handleSourceFooterBack = useCallback(() => {
        if (step === 'processing') {
            setStep('source');
            return;
        }
        setUploadedFile(null);
    }, [step]);

    const showSourceFooterBack = step === 'processing' || Boolean(uploadedFile);

    const extractionBody = (
        <V
            sx={{
                flex: 1,
                minHeight: 0,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            }}
        >
            {isInline ? (
                <>
                    <H
                        sx={{
                            width: '100%',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            px: 1.5,
                            pt: 1.5,
                            flexShrink: 0
                        }}
                    >
                        {panelTitle}
                        <IconButton onClick={onClose} size="small" sx={{ color: 'black' }}>
                            <Icons.Close />
                        </IconButton>
                    </H>
                    <Divider
                        flexItem
                        sx={{ alignSelf: 'stretch', width: '100%', flexShrink: 0, my: 1.5, borderColor: 'divider' }}
                    />
                </>
            ) : (
                <Divider
                    flexItem
                    sx={{
                        alignSelf: 'stretch',
                        width: '100%',
                        flexShrink: 0,
                        mb: 1.5,
                        borderColor: 'divider'
                    }}
                />
            )}
            {step === 'source' || step === 'processing' ? (
                <>
                    {step === 'processing' ? (
                        <V sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                            <LeadAIProcessing contentToken="shortlist.key.credits.ai.processing" />
                        </V>
                    ) : (
                        <KeyCreditsSourceStep
                            text={sourceText}
                            onTextChange={setSourceText}
                            uploadedFile={uploadedFile}
                            onUploadedFileChange={setUploadedFile}
                            activeOrganizationAccount={activeOrganizationAccount}
                        />
                    )}
                    <KeyCreditsSourceFooter
                        showBackButton={showSourceFooterBack}
                        onBack={handleSourceFooterBack}
                        onExtract={handleExtract}
                        extractLoading={step === 'processing'}
                        extractDisabled={step === 'processing' || !hasSource}
                    />
                </>
            ) : (
                <KeyCreditsReviewStep
                    credits={extractedCredits}
                    onBack={handleBackFromReview}
                    onSave={handleSave}
                    saveLoading={isSaving}
                    footerAccessory={profileUpdateFooterAccessory}
                />
            )}
        </V>
    );

    if (isInline) {
        return (
            <V fill sx={{ minHeight: 0, overflow: 'hidden', borderLeft: 1, borderColor: 'divider' }}>
                {extractionBody}
            </V>
        );
    }

    return (
        <SidePanel
            id="key-credits-ai-panel"
            title={panelTitle}
            securedActions={headerActions}
            inlineActions
        >
            {extractionBody}
        </SidePanel>
    );
}
